/**
 * Ray Cart Busy Guard
 *
 * Several Ray add-ons rewrite the cart AFTER it has rendered - gift wrap swaps the box size
 * (assets/ray-gift-wrap.js), tag cross-sell swaps line properties via remove+re-add
 * (assets/ray-tag-cross-sell.js), bundle discount sync rewrites line properties
 * (assets/ray-bundle-discount-sync.js). Each is a chain of /cart/*.js writes, so there is a
 * window where the cart on the server is mid-swap: reaching checkout inside it shows lines
 * that were about to be removed or re-priced.
 *
 * This counts in-flight cart writes at the one choke point they all share - fetch - and blocks
 * the checkout CTAs while any is outstanding, so no individual add-on has to opt in.
 *
 * Ray-owned add-on layered on top of the native components - see assets/ray-search-panel.js
 * for the same pattern elsewhere in this theme.
 */
(() => {
  if (window.RayCartBusyInitialized) return;

  window.RayCartBusyInitialized = true;

  // Bridges the gap between the sequential writes of one logical change (remove -> re-add) and
  // the cart re-render that follows, which would otherwise read as idle for a few frames.
  const SETTLE_DELAY = 300;

  // Never trap the shopper behind a request that hangs or never settles.
  const MAX_BUSY = 10000;

  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const CART_READ = /\/cart(\.js)?$/;
  const CHECKOUT_SELECTOR = '.cart__checkout-button, button[name="checkout"]';

  let pending = 0;
  let settleTimer = null;
  let watchdogTimer = null;

  // Buttons the guard itself disabled, so releasing never re-enables one that was disabled
  // server-side for another reason (an empty cart).
  const disabledByGuard = new WeakSet();

  const isBusy = () => document.documentElement.dataset.rayCartBusy === 'true';

  const applyState = (busy) => {
    document.documentElement.dataset.rayCartBusy = busy ? 'true' : 'false';

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (busy) {
        if (button.disabled) return;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        disabledByGuard.add(button);
      } else if (disabledByGuard.has(button)) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        disabledByGuard.delete(button);
      }
    });
  };

  const release = () => {
    pending = 0;

    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }

    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }

    applyState(false);
  };

  const begin = () => {
    pending += 1;

    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }

    if (watchdogTimer) clearTimeout(watchdogTimer);
    watchdogTimer = setTimeout(release, MAX_BUSY);

    if (!isBusy()) applyState(true);
  };

  const end = () => {
    pending = Math.max(0, pending - 1);

    if (pending > 0) return;

    if (settleTimer) clearTimeout(settleTimer);

    settleTimer = setTimeout(() => {
      settleTimer = null;

      if (pending === 0) release();
    }, SETTLE_DELAY);
  };

  const urlOf = (input) => {
    try {
      if (typeof input === 'string') return new URL(input, location.origin);
      if (input instanceof URL) return input;
      if (input && typeof input.url === 'string') return new URL(input.url, location.origin);
    } catch (error) {
      return null;
    }

    return null;
  };

  const nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    const url = urlOf(input);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();

    if (!url) return nativeFetch(input, init);

    const isWrite = method !== 'GET' && CART_WRITE.test(url.pathname);

    // The add-ons re-read the cart between their writes (ray-bundle-discount-sync.js loops
    // fetchCart() -> setLineProperties() per line), and that read can outlast SETTLE_DELAY on a
    // slow connection. Holding the window open across it keeps the chain from looking finished
    // halfway through; on its own a read never blocks checkout.
    const isChainedRead = method === 'GET' && CART_READ.test(url.pathname) && isBusy();

    if (!isWrite && !isChainedRead) return nativeFetch(input, init);

    begin();

    return nativeFetch(input, init).finally(end);
  };

  // The visual/disabled state above is not enough on its own: a disabled button still lets the
  // form be submitted implicitly (Enter in a field), and the accelerated buttons are iframes.
  document.addEventListener(
    'click',
    (event) => {
      if (!isBusy()) return;
      if (!event.target.closest?.(CHECKOUT_SELECTOR)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!isBusy()) return;

      const form = event.target;

      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  // The cart re-renders through the Section Rendering API, replacing the button nodes, so the
  // disabled state has to be re-applied to whatever is in the DOM now.
  new MutationObserver(() => {
    if (isBusy()) applyState(true);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
